import type { CreditType } from "@app/types/napi-v2-request-type";
import type { TrackingEvent } from "@app/types/tracking";
import type {
  ClickBackToSignin,
  ClickedAddPassenger,
  ClickedCancelUserPurchase,
  ClickedConfirmCancellation,
  ClickedConfirmNewPassword,
  ClickedConnect,
  ClickedDeletePassenger,
  ClickedDownloadTickets,
  ClickedDownloadTicketsTabs,
  ClickedEmailOptInCheckbox,
  ClickedForgotPassword,
  ClickedNewSearch,
  ClickedPurchase,
  ClickedResendEmailVerification,
  ClickedUpdatePassword,
  ConfirmAccountUpdate,
  ConfirmResendVerificationEmail,
  ConfirmSignIn,
  ConfirmSignUp,
  SignedIn,
  SignedUp,
  SigningMethod,
  SignupError,
  SubmittedUpdateAccountHolderForm,
  UpdatedPassword,
  AddToGoogleWalletError
} from "@app/types/tracking/account";
import type { AmplitudeEvent } from "@app/types/tracking/common";

import { AccountTrackingAction } from "./constants/tracking-action";

export function signedIn(
  user_id: number,
  sign_in_method: SigningMethod = "email"
): AmplitudeEvent<SignedIn> {
  return {
    action: AccountTrackingAction.SignedIn,
    category: "account",
    label: String(user_id),
    details: {
      account_signin_method: sign_in_method
    }
  };
}

export function confirmResendVerificationEmail(): AmplitudeEvent<ConfirmResendVerificationEmail> {
  return {
    action: AccountTrackingAction.ConfirmResendVerificationEmail,
    category: "account",
    details: {}
  };
}

export function clickBackToSignin(): AmplitudeEvent<ClickBackToSignin> {
  return {
    action: AccountTrackingAction.ClickBackToSignin,
    category: "account",
    details: {
      through_no_user_error_message: true
    }
  };
}

export function confirmSignIn(
  sign_in_method: SigningMethod = "email"
): AmplitudeEvent<ConfirmSignIn> {
  return {
    action: AccountTrackingAction.ConfirmSignIn,
    category: "account",
    details: {
      account_signin_method: sign_in_method
    }
  };
}

export function confirmSignUp(
  sign_up_method: SigningMethod = "email",
  auto_enrol_new_user?: boolean
): AmplitudeEvent<ConfirmSignUp> {
  return {
    action: AccountTrackingAction.ConfirmSignUp,
    category: "account",
    details: {
      account_signup_method: sign_up_method,
      auto_enrol_new_user
    }
  };
}

export function signedUp(
  fb_id?: string | undefined,
  auto_enrol_new_user?: boolean
): AmplitudeEvent<SignedUp> {
  return {
    action: AccountTrackingAction.SignedUp,
    category: "account",
    label: "",
    details: {
      signup_method: fb_id ? "facebook" : "classic",
      auto_enrol_new_user
    }
  };
}

export function submittedUpdateAccountHolderForm(): AmplitudeEvent<SubmittedUpdateAccountHolderForm> {
  return {
    action: AccountTrackingAction.SubmittedUpdateAccountHolderForm,
    category: "account",
    details: {}
  };
}

export function signupError(error_type: string): AmplitudeEvent<SignupError> {
  return {
    action: AccountTrackingAction.SignupError,
    category: "account",
    label: "",
    details: {
      error_type: error_type
    }
  };
}

export function clickedConnect(
  position: string,
  connect_type: string
): AmplitudeEvent<ClickedConnect> {
  return {
    action: AccountTrackingAction.AccountClickedConnect,
    category: "account",
    label: "",
    details: {
      position: position,
      connect_type: connect_type
    }
  };
}

export function clickedForgotPassword(): AmplitudeEvent<ClickedForgotPassword> {
  return {
    action: AccountTrackingAction.ClickedForgotPassword,
    category: "account",
    label: "",
    details: {}
  };
}

export function clickedDeletePassenger(): AmplitudeEvent<ClickedDeletePassenger> {
  return {
    action: AccountTrackingAction.ClickedDeletePassenger,
    category: "account",
    label: "",
    details: {}
  };
}

export function clickedAddPassenger(): AmplitudeEvent<ClickedAddPassenger> {
  return {
    action: AccountTrackingAction.ClickedAddPassenger,
    category: "account",
    label: "",
    details: {}
  };
}

export function clickedNewSearch(): AmplitudeEvent<ClickedNewSearch> {
  return {
    action: AccountTrackingAction.ClickedNewSearch,
    category: "account",
    label: "",
    details: { site_section: "my_trips" }
  };
}

export function clickedUpdatePassword(): AmplitudeEvent<ClickedUpdatePassword> {
  return {
    action: AccountTrackingAction.ClickedUpdatePassword,
    category: "account",
    label: "",
    details: {}
  };
}

export function addedPassengerProfile(): TrackingEvent {
  return {
    action: AccountTrackingAction.AddedPassengerProfile,
    category: "account",
    details: {}
  };
}

export function updatedPassword(): AmplitudeEvent<UpdatedPassword> {
  return {
    action: AccountTrackingAction.UpdatedPassword,
    category: "account",
    details: {}
  };
}

export function updatedPassengerProfile(): TrackingEvent {
  return accountAction(AccountTrackingAction.UpdatedPassengerProfile);
}

export function updatedUser(): TrackingEvent {
  return accountAction(AccountTrackingAction.UpdatedUser);
}

export function updatedBillingAddress(): TrackingEvent {
  return accountAction(AccountTrackingAction.UpdatedBillingAddress);
}

export function clickedUpdateBillingAddress(): TrackingEvent {
  return accountAction(AccountTrackingAction.ClickedUpdateBillingAddress);
}

export function confirmPassengerFormUpdate(
  field: "main_passenger" | "saved_passenger"
): AmplitudeEvent<ConfirmAccountUpdate> {
  return confirmAccountUpdate("passengers", field);
}

export function confirmAccountUpdate(
  section: "passengers" | "settings",
  field: string,
  field_value?: string
): AmplitudeEvent<ConfirmAccountUpdate> {
  return {
    action: AccountTrackingAction.ConfirmAccountUpdate,
    category: "account",
    details: { account_section: section, field, field_value }
  };
}

export function removedPassengerProfile(): TrackingEvent {
  return accountAction(AccountTrackingAction.RemovedPassengerProfile);
}

export function clickedDeleteCreditCard(): TrackingEvent {
  return accountAction(AccountTrackingAction.ClickedDeleteCreditCard);
}

export function removedCreditCard(): TrackingEvent {
  return accountAction(AccountTrackingAction.RemovedCreditCard);
}

export function clickedUpdateCreditCard(): TrackingEvent {
  return accountAction(AccountTrackingAction.ClickedUpdateCreditCard);
}

export function updatedCreditCard(): TrackingEvent {
  return accountAction(AccountTrackingAction.UpdatedCreditCard);
}

export function clickedDeleteBillingAddress(): TrackingEvent {
  return accountAction(AccountTrackingAction.ClickedDeleteBillingAddress);
}

export function deletedBillingAddress(): TrackingEvent {
  return accountAction(AccountTrackingAction.DeletedBillingAddress);
}

export function clickedUpdateEmailAddress(): TrackingEvent {
  return accountAction(AccountTrackingAction.ClickedUpdateEmailAddress);
}

export function updatedEmailAddress(): TrackingEvent {
  return accountAction(AccountTrackingAction.UpdatedEmailAddress);
}

export function addAddressClicked(): TrackingEvent {
  return accountAction(AccountTrackingAction.ClickedAddBillingAddress);
}

function accountAction(action: AccountTrackingAction): TrackingEvent {
  return {
    action,
    category: "account",
    details: {}
  };
}

export function clickedPurchase(
  is_archived: boolean
): AmplitudeEvent<ClickedPurchase> {
  return {
    action: AccountTrackingAction.ClickedPurchase,
    category: "account",
    label: "",
    details: { archived: is_archived }
  };
}

export function clickedCancelUserPurchase(
  cancellation_type: ClickedCancelUserPurchase["cancellation_type"]
): AmplitudeEvent<ClickedCancelUserPurchase> {
  return {
    action: AccountTrackingAction.ClickedCancelUserPurchase,
    category: "account",
    label: "",
    details: { site_section: "my-trips", cancellation_type }
  };
}

export function clickedConfirmCancellation(
  refund_method: CreditType = "original_payment_method"
): AmplitudeEvent<ClickedConfirmCancellation> {
  return {
    action: AccountTrackingAction.ClickedConfirmCancellation,
    category: "account",
    label: "",
    details: {
      site_section: "my-trips",
      refund_method
    }
  };
}

export function clickedResendEmailVerification(): AmplitudeEvent<ClickedResendEmailVerification> {
  return {
    action: AccountTrackingAction.ClickedResendEmailVerification,
    category: "account",
    label: "",
    details: {}
  };
}

export function clickedConfirmNewPassword(): AmplitudeEvent<ClickedConfirmNewPassword> {
  return {
    action: AccountTrackingAction.ClickedConfirmNewPassword,
    category: "account",
    label: "",
    details: {}
  };
}

export function clickedEmailOptInCheckbox(
  checked: boolean
): AmplitudeEvent<ClickedEmailOptInCheckbox> {
  return {
    action: AccountTrackingAction.ClickedEmailOptInCheckboxSignUp,
    category: "account",
    details: { checked }
  };
}

export function clickedDownloadTicketsTabs(
  ticket_tab_type: string
): AmplitudeEvent<ClickedDownloadTicketsTabs> {
  return {
    action: AccountTrackingAction.ClickedDownloadTicketsTabs,
    category: "account",
    details: { ticket_tab_type }
  };
}

export function clickedDownloadTickets(
  ticket_type: string
): AmplitudeEvent<ClickedDownloadTickets> {
  return {
    action: AccountTrackingAction.ClickedDownloadTickets,
    category: "account",
    details: { ticket_type }
  };
}

export function addToGoogleWalletError(
  error: unknown
): AmplitudeEvent<AddToGoogleWalletError> {
  return {
    action: AccountTrackingAction.AddToGoogleWalletError,
    category: "account",
    details: { error }
  };
}
